import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRole } from "../context/RoleContext";
import { createTrip } from "../lib/api";
import { DestinationBadge } from "../components/DestinationBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Check, ChevronLeft, ChevronRight, Plane, Hotel, MapPin, User, FileText, Factory, Building2 } from "lucide-react";
import { cn } from "../lib/utils";

const STEPS = [
  { id: 1, label: "Destination", icon: MapPin },
  { id: 2, label: "Personal", icon: User },
  { id: 3, label: "Flight", icon: Plane },
  { id: 4, label: "Lodging", icon: Hotel },
  { id: 5, label: "Notes", icon: FileText },
];

const initialForm = {
  destination: "",
  purpose: "",
  full_name: "",
  email: "",
  phone: "",
  whatsapp: "",
  arrival_date: "",
  arrival_time: "",
  departure_date: "",
  departure_time: "",
  airline: "",
  flight_number: "",
  origin_city: "",
  destination_airport: "",
  accommodation_name: "",
  accommodation_type: "hotel",
  accommodation_address: "",
  primary_location: "",
  transportation_notes: "",
  emergency_contact: "",
  special_notes: "",
};

export default function TravelerSubmit() {
  const navigate = useNavigate();
  const { setTravelerName, setActiveTripId } = useRole();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canNext = () => {
    if (step === 1) return !!form.destination;
    if (step === 2) return form.full_name.trim() && form.email.trim() && form.arrival_date && form.departure_date;
    return true;
  };

  const next = () => step < STEPS.length && setStep(step + 1);
  const back = () => step > 1 && setStep(step - 1);

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = { ...form, expected_locations: [], tags: [] };
      const trip = await createTrip(payload);
      setTravelerName(trip.full_name);
      setActiveTripId(trip.id);
      toast.success("Travel record submitted — check your WhatsApp coordinator card next.");
      navigate("/traveler/status");
    } catch (e) {
      toast.error("Submission failed. Please check your inputs.");
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  const progress = ((step - 1) / (STEPS.length - 1)) * 100;

  return (
    <div className="min-h-[calc(100vh-4rem)] px-4 sm:px-6 lg:px-8 py-8 lg:py-12" data-testid="traveler-submit-page">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <p className="label-eyebrow mb-2">New Travel Submission</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-white">Tell us about your trip</h1>
          <p className="text-sm text-zinc-400 mt-2">Mobile-first, no account required. Your coordinator will follow up to confirm.</p>
        </div>

        {/* Progress bar */}
        <div className="glass-card p-4 sm:p-5 mb-6" data-testid="form-progress">
          <div className="flex items-center justify-between mb-3">
            {STEPS.map((s) => {
              const done = s.id < step;
              const active = s.id === step;
              const Icon = s.icon;
              return (
                <div key={s.id} className="flex-1 flex flex-col items-center gap-1">
                  <div
                    className={cn(
                      "h-9 w-9 rounded-full flex items-center justify-center border transition-all",
                      done && "bg-[#F07D00] border-[#F07D00] text-white",
                      active && "bg-[#F07D00]/20 border-[#F07D00] text-[#F07D00] shadow-[0_0_16px_rgba(240,125,0,0.4)]",
                      !done && !active && "bg-white/5 border-white/10 text-zinc-500"
                    )}
                  >
                    {done ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                  </div>
                  <span className={cn("text-[10px] font-semibold tracking-wider uppercase hidden sm:block", active ? "text-white" : "text-zinc-500")}>
                    {s.label}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="h-1 bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#F07D00] to-[#FF8C1A] shadow-[0_0_10px_rgba(240,125,0,0.5)] transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <div className="glass-card p-6 sm:p-8 animate-fade-up" key={step}>
          {step === 1 && (
            <div className="space-y-5">
              <h2 className="font-heading text-xl font-medium text-white">Where are you going?</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { key: "Monterrey", icon: Factory, title: "Monterrey Vertiv Plant", blurb: "Industrial · Nuevo León" },
                  { key: "Apodaca", icon: Building2, title: "Apodaca Vertiv Office", blurb: "Office · Nuevo León" },
                ].map(({ key, icon: Icon, title, blurb }) => (
                  <button
                    key={key}
                    onClick={() => update("destination", key)}
                    data-testid={`destination-${key.toLowerCase()}-button`}
                    className={cn(
                      "p-5 rounded-xl border text-left transition-all",
                      form.destination === key
                        ? "bg-[#F07D00]/10 border-[#F07D00] shadow-[0_0_24px_rgba(240,125,0,0.2)]"
                        : "bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20"
                    )}
                  >
                    <Icon className={cn("h-7 w-7 mb-3", form.destination === key ? "text-[#F07D00]" : "text-zinc-400")} />
                    <div className="font-heading text-lg font-semibold text-white">{title}</div>
                    <div className="text-xs text-zinc-400 mt-1">{blurb}</div>
                  </button>
                ))}
              </div>

              <div className="pt-2">
                <Label htmlFor="purpose" className="label-eyebrow">Purpose of trip</Label>
                <Input
                  id="purpose"
                  value={form.purpose}
                  onChange={(e) => update("purpose", e.target.value)}
                  placeholder="e.g., Executive review, Partner event, Customer workshop"
                  className="glass-input mt-2"
                  data-testid="input-purpose"
                />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <h2 className="font-heading text-xl font-medium text-white">Personal & dates</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Full name" required>
                  <Input value={form.full_name} onChange={(e) => update("full_name", e.target.value)} className="glass-input" data-testid="input-full-name" placeholder="Jane Chen" />
                </Field>
                <Field label="Email" required>
                  <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className="glass-input" data-testid="input-email" placeholder="jane@carreytransport.com" />
                </Field>
                <Field label="Phone">
                  <Input value={form.phone} onChange={(e) => update("phone", e.target.value)} className="glass-input" data-testid="input-phone" placeholder="+1-408-555-0100" />
                </Field>
                <Field label="WhatsApp">
                  <Input value={form.whatsapp} onChange={(e) => update("whatsapp", e.target.value)} className="glass-input" data-testid="input-whatsapp" placeholder="+14085550100" />
                </Field>
                <Field label="Arrival date" required>
                  <Input type="date" value={form.arrival_date} onChange={(e) => update("arrival_date", e.target.value)} className="glass-input" data-testid="input-arrival-date" />
                </Field>
                <Field label="Arrival time">
                  <Input type="time" value={form.arrival_time} onChange={(e) => update("arrival_time", e.target.value)} className="glass-input" data-testid="input-arrival-time" />
                </Field>
                <Field label="Departure date" required>
                  <Input type="date" value={form.departure_date} onChange={(e) => update("departure_date", e.target.value)} className="glass-input" data-testid="input-departure-date" />
                </Field>
                <Field label="Departure time">
                  <Input type="time" value={form.departure_time} onChange={(e) => update("departure_time", e.target.value)} className="glass-input" data-testid="input-departure-time" />
                </Field>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <h2 className="font-heading text-xl font-medium text-white">Flight details</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Airline"><Input value={form.airline} onChange={(e) => update("airline", e.target.value)} className="glass-input" data-testid="input-airline" placeholder="United Airlines" /></Field>
                <Field label="Flight number"><Input value={form.flight_number} onChange={(e) => update("flight_number", e.target.value)} className="glass-input" data-testid="input-flight-number" placeholder="UA 1482" /></Field>
                <Field label="Origin city"><Input value={form.origin_city} onChange={(e) => update("origin_city", e.target.value)} className="glass-input" data-testid="input-origin-city" placeholder="Chicago (ORD)" /></Field>
                <Field label="Destination airport"><Input value={form.destination_airport} onChange={(e) => update("destination_airport", e.target.value)} className="glass-input" data-testid="input-destination-airport" placeholder="Monterrey (MTY)" /></Field>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4">
              <h2 className="font-heading text-xl font-medium text-white">Lodging & primary location</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Accommodation name"><Input value={form.accommodation_name} onChange={(e) => update("accommodation_name", e.target.value)} className="glass-input" data-testid="input-accommodation-name" placeholder="Quinta Real Monterrey" /></Field>
                <Field label="Type">
                  <select value={form.accommodation_type} onChange={(e) => update("accommodation_type", e.target.value)} className="glass-input h-10 px-3 rounded-md w-full" data-testid="select-accommodation-type">
                    <option value="hotel">Hotel</option>
                    <option value="airbnb">Airbnb / Rental</option>
                    <option value="resort">Resort</option>
                    <option value="other">Other</option>
                  </select>
                </Field>
                <Field label="Address" className="sm:col-span-2"><Input value={form.accommodation_address} onChange={(e) => update("accommodation_address", e.target.value)} className="glass-input" data-testid="input-accommodation-address" placeholder="Street, City, State" /></Field>
                <Field label="Primary work location" className="sm:col-span-2"><Input value={form.primary_location} onChange={(e) => update("primary_location", e.target.value)} className="glass-input" data-testid="input-primary-location" placeholder="Carrey Monterrey Office" /></Field>
              </div>
            </div>
          )}

          {step === 5 && (
            <div className="space-y-4">
              <h2 className="font-heading text-xl font-medium text-white">Notes & emergency</h2>
              <Field label="Pickup / drop-off notes"><Textarea value={form.transportation_notes} onChange={(e) => update("transportation_notes", e.target.value)} className="glass-input min-h-20" data-testid="input-transportation" placeholder="Preferred pickup window, accessibility needs, multiple stops, special requests, etc." /></Field>
              <Field label="Emergency / alternate contact"><Input value={form.emergency_contact} onChange={(e) => update("emergency_contact", e.target.value)} className="glass-input" data-testid="input-emergency" placeholder="Name, relationship, phone" /></Field>
              <Field label="Special notes or requests"><Textarea value={form.special_notes} onChange={(e) => update("special_notes", e.target.value)} className="glass-input min-h-24" data-testid="input-notes" placeholder="Dietary restrictions, accessibility, etc." /></Field>

              {/* Summary */}
              <div className="mt-6 p-4 rounded-lg bg-black/30 border border-white/10">
                <p className="label-eyebrow mb-3">Review summary</p>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2"><span className="text-zinc-500">Destination:</span><DestinationBadge destination={form.destination} size="sm" /></div>
                  <div><span className="text-zinc-500">Name:</span> <span className="text-white">{form.full_name || "—"}</span></div>
                  <div><span className="text-zinc-500">Dates:</span> <span className="text-white">{form.arrival_date || "—"} → {form.departure_date || "—"}</span></div>
                  <div><span className="text-zinc-500">Flight:</span> <span className="text-white">{form.airline} {form.flight_number || "—"}</span></div>
                  <div><span className="text-zinc-500">Lodging:</span> <span className="text-white">{form.accommodation_name || "—"}</span></div>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between pt-8 mt-2 border-t border-white/10">
            <Button
              variant="ghost"
              onClick={back}
              disabled={step === 1}
              className="text-zinc-400 hover:text-white hover:bg-white/5 disabled:opacity-30"
              data-testid="form-back-button"
            >
              <ChevronLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            {step < STEPS.length ? (
              <Button onClick={next} disabled={!canNext()} className="brand-btn" data-testid="form-next-button">
                Continue <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={submit} disabled={submitting} className="brand-btn" data-testid="form-submit-button">
                {submitting ? "Submitting..." : "Submit Trip"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, children, className }) {
  return (
    <div className={className}>
      <Label className="label-eyebrow flex items-center gap-1">
        {label} {required && <span className="text-[#F07D00]">*</span>}
      </Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
